import { Paper, Skeleton, Stack, Typography } from "@mui/material";
import type { FC, KeyboardEvent, ReactNode } from "react";

interface KpiCardProps {
  label: string;
  value: string | number;
  icon?: ReactNode;
  color?: string;
  isLoading?: boolean;
  onClick?: () => void;
  fullWidth?: boolean;
}

export const KpiCard: FC<KpiCardProps> = ({
  label,
  value,
  icon,
  color = "#05204c",
  isLoading,
  onClick,
  fullWidth,
}) => {
  const clickable = !!onClick && !isLoading;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Paper
      elevation={2}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      sx={{
        flex: fullWidth ? "1 1 auto" : "1 1 180px",
        minWidth: fullWidth ? 0 : 160,
        maxWidth: fullWidth ? "none" : 240,
        width: fullWidth ? "100%" : undefined,
        padding: "1.25rem 1rem",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0.5rem",
        textAlign: "center",
        transition: "box-shadow 0.2s ease",
        cursor: clickable ? "pointer" : "default",
        "&:hover": clickable
          ? {
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            }
          : {
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            },
        "&:focus-visible": clickable
          ? {
              outline: "2px solid #00b4d8",
              outlineOffset: 2,
            }
          : undefined,
      }}
    >
      {icon && (
        <Stack sx={{ color, fontSize: "1.5rem" }}>{icon}</Stack>
      )}
      {isLoading ? (
        <Skeleton variant="text" width={60} height={42} />
      ) : (
        <Typography
          sx={{
            fontSize: "1.75rem",
            fontWeight: 700,
            color,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: "0.85rem",
          color: "#68686f",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Paper>
  );
};
